import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, Users, Clock, Loader2, Target, Plus, Send, Eye } from "lucide-react";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import CreateChallengeDialog from "@/components/CreateChallengeDialog";
import ChallengeSubmissionDialog from "@/components/ChallengeSubmissionDialog";
import { notifyChallengeJoin } from "@/lib/notifications";
import { ChallengeParticipantsDialog } from "@/components/ChallengeParticipantsDialog";

interface Challenge {
  id: string;
  title: string;
  description: string;
  skill_category: string;
  points: number;
  start_date: string;
  end_date: string;
  participants_count: number;
  creator_id: string | null;
}

interface UserChallenge {
  challenge_id: string;
  progress: number;
  completed_at: string | null;
}

const SeasonalChallenges = () => {
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [userChallenges, setUserChallenges] = useState<UserChallenge[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [submissionDialog, setSubmissionDialog] = useState<{
    open: boolean;
    challenge: Challenge | null;
  }>({ open: false, challenge: null });
  const [participantsDialog, setParticipantsDialog] = useState<{
    open: boolean;
    challengeId: string;
    challengeTitle: string;
  }>({ open: false, challengeId: '', challengeTitle: '' });
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchChallenges();
    if (user) fetchUserChallenges();
  }, [user]);

  const fetchChallenges = async () => {
    try {
      const { data, error } = await supabase
        .from('seasonal_challenges')
        .select('*')
        .gte('end_date', new Date().toISOString())
        .order('start_date', { ascending: true });

      if (error) throw error;
      setChallenges(data || []);
    } catch (error: any) {
      console.error('Error fetching challenges:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserChallenges = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('user_seasonal_challenges')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;
      setUserChallenges(data || []);
    } catch (error: any) {
      console.error('Error fetching user challenges:', error);
    }
  };

  const joinChallenge = async (challengeId: string) => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to join challenges",
        variant: "destructive"
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('user_seasonal_challenges')
        .insert({
          user_id: user.id,
          challenge_id: challengeId,
          progress: 0
        });

      if (error) throw error;

      const challenge = challenges.find(c => c.id === challengeId);
      
      // Update participants count
      await supabase
        .from('seasonal_challenges')
        .update({ participants_count: (challenge?.participants_count || 0) + 1 })
        .eq('id', challengeId);

      // Send notification to challenge creator
      if (challenge?.creator_id && challenge.creator_id !== user.id) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('name')
          .eq('id', user.id)
          .single();
        
        await notifyChallengeJoin(
          challenge.creator_id,
          profile?.name || 'Someone',
          challenge.title
        );
      }

      toast({
        title: "Challenge joined!",
        description: "Good luck on your learning journey!"
      });

      fetchUserChallenges();
      fetchChallenges();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const getUserProgress = (challengeId: string) => {
    return userChallenges.find(uc => uc.challenge_id === challengeId);
  };

  const openSubmissionDialog = (challenge: Challenge) => {
    setSubmissionDialog({ open: true, challenge });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pb-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <Navigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="gradient-primary text-white p-6 pb-12 rounded-b-3xl shadow-strong">
        <div className="container max-w-md mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Target className="h-8 w-8" />
              <div>
                <h1 className="text-2xl font-bold">Challenges</h1>
                <p className="text-sm text-white/80">Create & join skill challenges</p>
              </div>
            </div>
            {user && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setCreateDialogOpen(true)}
                className="bg-white/20 hover:bg-white/30 text-white border-0"
              >
                <Plus className="h-4 w-4 mr-1" />
                Create
              </Button>
            )}
          </div>
        </div>
      </header>

      <CreateChallengeDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onChallengeCreated={fetchChallenges}
      />

      {submissionDialog.challenge && (
        <ChallengeSubmissionDialog
          open={submissionDialog.open}
          onOpenChange={(open) => setSubmissionDialog({ ...submissionDialog, open })}
          challenge={submissionDialog.challenge}
          onSubmitted={() => {
            fetchUserChallenges();
            fetchChallenges();
          }}
        />
      )}

      <main className="container max-w-md mx-auto px-4 -mt-6 space-y-4">
        {challenges.length === 0 ? (
          <Card className="p-8 text-center shadow-soft">
            <Target className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-bold text-lg mb-2">No Active Challenges</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {user ? "Be the first to create a challenge!" : "Login to create challenges and participate!"}
            </p>
            {user ? (
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Challenge
              </Button>
            ) : (
              <Button onClick={() => navigate('/auth')}>
                Get Started
              </Button>
            )}
          </Card>
        ) : (
          challenges.map((challenge) => {
            const userProgress = getUserProgress(challenge.id);
            const isJoined = !!userProgress;
            const isCompleted = userProgress?.completed_at !== null && userProgress?.completed_at !== undefined;
            const daysLeft = formatDistanceToNow(new Date(challenge.end_date), { addSuffix: true });

            return (
              <Card key={challenge.id} className="p-5 shadow-soft hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-bold text-lg mb-1">{challenge.title}</h3>
                    <Badge variant="secondary" className="mb-2">
                      {challenge.skill_category}
                    </Badge>
                  </div>
                  <Trophy className="h-6 w-6 text-accent-orange" />
                </div>

                <p className="text-sm text-muted-foreground mb-4">
                  {challenge.description}
                </p>

                <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
                  {challenge.creator_id === user?.id ? (
                    <button
                      onClick={() => setParticipantsDialog({
                        open: true,
                        challengeId: challenge.id,
                        challengeTitle: challenge.title
                      })}
                      className="flex items-center gap-1 hover:text-primary transition-colors"
                    >
                      <Eye className="h-3 w-3" />
                      <span>{challenge.participants_count} joined</span>
                    </button>
                  ) : (
                    <div className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      <span>{challenge.participants_count} joined</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    <Trophy className="h-3 w-3" />
                    <span>{challenge.points} points</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    <span>Ends {daysLeft}</span>
                  </div>
                </div>

                {isJoined && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-muted-foreground">Your Progress</span>
                      <span className="font-semibold">{userProgress.progress}%</span>
                    </div>
                    <Progress value={userProgress.progress} className="h-2" />
                  </div>
                )}

                {isCompleted ? (
                  <Badge className="w-full justify-center bg-green-500 hover:bg-green-600">
                    ✓ Completed
                  </Badge>
                ) : isJoined ? (
                  <Button 
                    className="w-full"
                    onClick={() => openSubmissionDialog(challenge)}
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Submit Entry
                  </Button>
                ) : (
                  <Button 
                    className="w-full gradient-primary text-white"
                    onClick={() => joinChallenge(challenge.id)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Join Challenge
                  </Button>
                )}
              </Card>
            );
          })
        )}
      </main>

      <ChallengeParticipantsDialog
        open={participantsDialog.open}
        onOpenChange={(open) => setParticipantsDialog({ ...participantsDialog, open })}
        challengeId={participantsDialog.challengeId}
        challengeTitle={participantsDialog.challengeTitle}
      />

      <Navigation />
    </div>
  );
};

export default SeasonalChallenges;
